import { zodResolver } from '@hookform/resolvers/zod';
import {
  IQuoteSettingApprovalSchema,
  QuoteSettingApprovalSchema,
} from '@monetize/types/app';
import {
  ExperienceConfiguration,
  ExperienceConfigurationKeys,
  QuoteAndContractSettingsConfig,
  QuoteAndContractSettingsConfigWithKeySchema,
} from '@monetize/types/experience-config';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  useGetExperienceConfiguration,
  useUpdateExperienceConfiguration,
} from '../../../../../api/experienceConfigurationService';
import {
  AsyncStatusIndicator,
  MAccordion,
  MAccordionCustomButton,
  MBox,
  MButton,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MStack,
  MSwitch,
  MText,
} from '../../../../../components/Monetize';
import MEditor from '../../../../../components/Monetize/MEditor';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../../hooks';
import { useAsyncStatus } from '../../../../../hooks/useAsyncStatus';
import { useScrollToHighlightedSection } from '../../../../../hooks/useScrollToHighlightedSection';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { useSettingsSectionHighlights } from '../../../../../hooks/useSettingsSectionHighlights';
import { SettingsSection } from '../../../components/SettingsSection';
import {
  QUOTE_SETTING_KEYS,
  getQuoteSettingsSectionKeys,
} from '../quoteSettingsConfig';
import { ExperienceConfigRoleBasedSetting } from './QuoteExperienceConfigSettings/RoleBasedSetting';

export const quoteSettingApprovalsOwnedRoleKeys: (keyof QuoteAndContractSettingsConfig)[] =
  ['allowRetainApprovalsRoles'];

interface QuoteSettingApprovalsProps {
  approvalData: IQuoteSettingApprovalSchema;
  onSubmit: (data: IQuoteSettingApprovalSchema) => Promise<void>;
}

export const QuoteSettingApprovals = ({
  approvalData,
  onSubmit,
}: QuoteSettingApprovalsProps) => {
  const highlights = useSettingsSectionHighlights(
    getQuoteSettingsSectionKeys(QUOTE_SETTING_KEYS.approval),
  );
  const [isLoading, setIsLoading] = useState(false);
  const { status: approvalStatus, track: trackApproval } = useAsyncStatus();
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
  } = useForm<IQuoteSettingApprovalSchema>({
    resolver: zodResolver(QuoteSettingApprovalSchema),
    mode: 'onChange',
    defaultValues: {
      quoteApprovalNotes: {
        status: true,
        approvalNote: '',
      },
    },
  });

  const { data: experienceConfigData, isLoading: isLoadingExperienceConfig } =
    useGetExperienceConfiguration();

  useScrollToHighlightedSection(
    highlights,
    !!experienceConfigData && !isLoadingExperienceConfig,
  );

  const { mutate: doUpdateExperienceConfiguration, isPending } =
    useUpdateExperienceConfiguration({
      meta: { showErrorToast: true },
      onSuccess: (updatedData: ExperienceConfiguration) => {
        experienceConfigReset({
          key: ExperienceConfigurationKeys.quoteAndContractSettings,
          configuration:
            updatedData.configuration as QuoteAndContractSettingsConfig,
        });
      },
    });

  const {
    handleSubmit: experienceConfigHandleSubmit,
    reset: experienceConfigReset,
    setValue: experienceConfigSetValue,
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(QuoteAndContractSettingsConfigWithKeySchema),
    defaultValues: {
      key: ExperienceConfigurationKeys.quoteAndContractSettings,
      configuration: experienceConfigData?.quoteAndContractSettings || {},
    },
  });

  useEffect(() => {
    if (experienceConfigData) {
      experienceConfigReset({
        key: ExperienceConfigurationKeys.quoteAndContractSettings,
        configuration: experienceConfigData.quoteAndContractSettings,
      });
    }
  }, [experienceConfigData, experienceConfigReset]);

  useEffect(() => {
    if (approvalData) {
      reset({
        ...approvalData,
        quoteApprovalNotes: {
          ...approvalData.quoteApprovalNotes,
          approvalNote: approvalData.quoteApprovalNotes.approvalNote || '',
        },
      });
    }
  }, [approvalData]);

  const handleSubmitNote = async (data: IQuoteSettingApprovalSchema) => {
    try {
      setIsLoading(true);
      await onSubmit(data);
    } catch (err: any) {
      handleApiErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  const formApprovalStatus = watch('quoteApprovalNotes.status');

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  const shouldOpenAccordion = Object.values(highlights).some(Boolean);
  useEffect(() => {
    if (shouldOpenAccordion) {
      setAccordionValue(['0']);
    }
  }, [shouldOpenAccordion, setAccordionValue]);

  return (
    <MStack w="100%" data-testid="approval-notes-config">
      <MAccordion
        multiple
        value={accordionValue}
        onValueChange={({ value }) => handleAccordionChange(value)}
      >
        <MSettingAccordionItem
          value="0"
          id={QUOTE_SETTING_KEYS.approval}
          {...getHighlightStyles(highlights[QUOTE_SETTING_KEYS.approval])}
          renderAccordionButton={({ isExpanded }: { isExpanded: boolean }) => (
            <MAccordionCustomButton isExpanded={isExpanded} label="Approval" />
          )}
          borderWidth="1px"
        >
          {experienceConfigData && (
            <form
              onSubmit={experienceConfigHandleSubmit((formValues) =>
                doUpdateExperienceConfiguration(formValues),
              )}
            >
              <SettingsSection
                id="allowRetainApprovalsRoles"
                {...getHighlightStyles(highlights['allowRetainApprovalsRoles'])}
              >
                <ExperienceConfigRoleBasedSetting
                  metadataKey="allowRetainApprovalsRoles"
                  value={
                    experienceConfigData.quoteAndContractSettings
                      .allowRetainApprovalsRoles
                  }
                  isLoading={isPending}
                  onSave={(newValue) => {
                    experienceConfigSetValue(
                      'configuration.allowRetainApprovalsRoles',
                      newValue,
                    );
                    experienceConfigHandleSubmit((formValues) =>
                      doUpdateExperienceConfiguration(formValues),
                    )();
                  }}
                />
              </SettingsSection>
            </form>
          )}
          <form
            onSubmit={handleSubmit(handleSubmitNote)}
            data-testid="quote-setting-form"
          >
            <SettingsSection
              id={QUOTE_SETTING_KEYS.quoteApprovalNotes}
              {...getHighlightStyles(
                highlights[QUOTE_SETTING_KEYS.quoteApprovalNotes],
              )}
              showDivider={false}
            >
              <MFlex
                justify="space-between"
                w="100%"
                data-testid="approval-notes-check"
              >
                <MBox>
                  <MText fontSize="md" fontWeight="600">
                    Approval Notes
                  </MText>
                  <MText fontSize="sm" mt="2">
                    This message will show up before a quote goes to the review
                    screen
                  </MText>
                </MBox>
                <MFormField
                  error={errors?.quoteApprovalNotes?.status}
                  onClick={(ev) => ev.stopPropagation()}
                  w="auto"
                >
                  <Controller
                    name="quoteApprovalNotes.status"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MBox position="relative">
                        <MSwitch
                          isChecked={value}
                          size="md"
                          isDisabled={approvalStatus === 'loading'}
                          onChange={(ev) => {
                            onChange(ev.target.checked);
                            trackApproval(() => handleSubmit(onSubmit)());
                          }}
                          {...rest}
                        />
                        <AsyncStatusIndicator status={approvalStatus} />
                      </MBox>
                    )}
                  />
                </MFormField>
              </MFlex>
              <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
                <MGridItem colSpan={12}>
                  <MFormField error={errors?.quoteApprovalNotes?.approvalNote}>
                    <Controller
                      name="quoteApprovalNotes.approvalNote"
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <MEditor
                          autoScroll
                          handleEditorChange={onChange}
                          boxProps={{ pb: 0, minHeight: 125 }}
                          init={{ height: 125 }}
                          disabled={!formApprovalStatus}
                          value={value || ''}
                          {...rest}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
                <MGridItem colSpan={12} mt="4">
                  <MFlex justify="end">
                    <MButton
                      variant="primary"
                      onClick={handleSubmit(handleSubmitNote)}
                      isDisabled={
                        !isDirty || !isValid || !formApprovalStatus || isLoading
                      }
                      isLoading={isLoading}
                    >
                      Save
                    </MButton>
                  </MFlex>
                </MGridItem>
              </MGrid>
            </SettingsSection>
          </form>
        </MSettingAccordionItem>
      </MAccordion>
    </MStack>
  );
};
